import Cookies from 'js-cookie';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

// Below interfaces are based on CookieYes docs as types aren't provided

interface CookieYesDetail {
  analytics: 'yes' | 'no';
  advertisement: 'yes' | 'no';
  functional: 'yes' | 'no';
  consent: 'yes' | 'no';
  action: 'yes' | 'no';
  // other or uncategorized?
}
interface CookieYesEvent extends CustomEvent {
  detail: CookieYesDetail;
}

/** Parse the custom CookieYes consent cookie */
const parseCookieYesConsent = () => {
  // this is buried a bit in Cookie Yes docs, but banner will set 'cookieyes-consent' cookie
  // https://www.cookieyes.com/documentation/obtain-the-consent-id/
  const cookieYesConsentRaw = Cookies.get('cookieyes-consent');
  if (!cookieYesConsentRaw) return { analytics: false, initialized: false };
  const parsed: CookieYesDetail = Object.fromEntries(
    cookieYesConsentRaw.split(',').map((p) => p.split(':')),
  );
  return {
    analytics: parsed.analytics === 'yes',
    initialized: parsed.action === 'yes',
  };
};

// for now we just use analytics for HotJar and Mixpanel
// initialized will track if user has interacted with CookieYes banner
type ConsentState = { analytics: boolean; initialized: boolean };
type ConsentContextType = { consent: ConsentState };
const ConsentContext = createContext<ConsentContextType | null>(null);

/** Provider to manage consent state. Includes listener for CookieYes consent changes via banner events */
export const ConsentProvider = ({ children }: { children: ReactNode }) => {
  const [consent, setConsent] = useState<ConsentState>(parseCookieYesConsent);
  useEffect(() => {
    const handleConsentChange = (e: Event) => {
      const consentDetail = (e as CookieYesEvent).detail;
      if (consentDetail) {
        setConsent({
          analytics: consentDetail.analytics === 'yes',
          initialized: true,
        });
      }
    };
    document.addEventListener('cookieyes_consent_update', handleConsentChange);
    return () => {
      document.removeEventListener('cookieyes_consent_update', handleConsentChange);
    };
  }, []);
  return <ConsentContext.Provider value={{ consent: consent }}>{children}</ConsentContext.Provider>;
};

export const useConsent = () => {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return context;
};
